import { Building2, LayoutGrid } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { PermissionErrorPanel } from '@/components/auth/PermissionErrorPanel';
import { PageChrome } from '@/components/layout/page-chrome';
import { StatePanel } from '@/components/ui/StatePanel';
import { usePermission, usePermissionState } from '@/hooks/usePermission';
import { useActiveCompany } from '@/lib/active-company-context';

import { ActivityCard } from './components/ActivityCard';
import { DeadlinesCard } from './components/DeadlinesCard';
import { KpiTiles } from './components/KpiTiles';
import { NormComplianceCard } from './components/NormComplianceCard';
import { PendingSignaturesCard } from './components/PendingSignaturesCard';
import { TimeHealthCard } from './components/TimeHealthCard';
import { DASHBOARD_PERMISSION } from './use-dashboard';
import { skeletonLine } from '@/components/ui/skeleton';

// Panel del QMS (so=430, spec §3; mockup 01): la pantalla de inicio de todos los roles.
// Cada tarjeta aparece con su permiso y carga, falla y se reintenta por separado (D1).

const PERMS = {
  master: DASHBOARD_PERMISSION,
  productivity: 'quality:productivity:read',
  rework: 'quality:rework:read',
  audit: 'system:audit:read',
} as const;

const BREADCRUMB = [
  { label: 'QMS', strong: true },
  { label: 'Panel del QMS', strong: true },
];

function Frame({ children }: { children: ReactNode }): ReactElement {
  return (
    <>
      <PageChrome breadcrumb={BREADCRUMB} navContext="Panel del QMS" />
      {children}
    </>
  );
}

/** Fila de dos columnas; con una sola tarjeta, la tarjeta ocupa todo el ancho. */
function Row({ children }: { children: (ReactElement | false)[] }): ReactElement | null {
  const cards = children.filter((c): c is ReactElement => c !== false);
  if (cards.length === 0) {
    return null;
  }
  return <div className={cards.length === 2 ? 'g-2 dash-row' : 'dash-row'}>{cards}</div>;
}

export function SgcDashboard(): ReactElement {
  const perm = usePermissionState(DASHBOARD_PERMISSION);
  const can = {
    master: usePermission(PERMS.master),
    productivity: usePermission(PERMS.productivity),
    rework: usePermission(PERMS.rework),
    audit: usePermission(PERMS.audit),
  };
  const {
    activeCompanyId,
    activeCompanyName,
    crossCompany,
    isLoading: companyLoading,
  } = useActiveCompany();

  if (perm.state === 'error') {
    return (
      <Frame>
        <PermissionErrorPanel error={perm.error} onRetry={perm.retry} retrying={perm.retrying} />
      </Frame>
    );
  }
  if (perm.state === 'loading' || companyLoading) {
    return (
      <Frame>
        <div className="tiles cols-5" aria-busy="true">
          <span className="sr-only">Cargando el panel</span>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="tile" aria-hidden="true">
              <span className={skeletonLine({ width: 60 })} />
              <span className={skeletonLine({ width: 40, height: 'md' })} />
            </div>
          ))}
        </div>
      </Frame>
    );
  }
  if (!can.master && !can.productivity && !can.rework && !can.audit) {
    return (
      <Frame>
        <StatePanel
          icon={<LayoutGrid />}
          title="Tu rol no tiene indicadores en el panel"
          role="status"
        >
          Pide acceso a Calidad si necesitas ver los indicadores del QMS.
        </StatePanel>
      </Frame>
    );
  }
  if (!activeCompanyId) {
    return (
      <Frame>
        <StatePanel icon={<Building2 />} title="Selecciona una empresa activa" role="status">
          Elige una empresa activa en la barra superior.
        </StatePanel>
      </Frame>
    );
  }

  const id = activeCompanyId;
  return (
    <Frame>
      <div className="pagehead rise">
        <div className="grow">
          <h1 style={{ marginBottom: 4 }}>Panel del QMS</h1>
          <p style={{ maxWidth: '70ch' }}>
            Estado documental de {activeCompanyName ?? 'la empresa activa'}: vigencia, vencimientos,
            firmas pendientes y tiempos del mes.
          </p>
        </div>
      </div>
      {can.master && <KpiTiles companyId={id} />}
      <Row>
        {[
          can.master && <DeadlinesCard key="deadlines" companyId={id} />,
          can.master && <NormComplianceCard key="norms" companyId={id} />,
        ]}
      </Row>
      <Row>
        {[
          can.master && <PendingSignaturesCard key="pending" companyId={id} />,
          (can.productivity || can.rework) && (
            <TimeHealthCard
              key="time"
              companyId={id}
              canDeviation={can.productivity}
              canRework={can.rework}
            />
          ),
        ]}
      </Row>
      {can.audit && (
        <div className="dash-row">
          <ActivityCard companyId={id} crossCompany={crossCompany} />
        </div>
      )}
    </Frame>
  );
}
